import React from 'react'
import { IconDownload, IconMinimize } from '@tabler/icons-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Dialog, DialogPortal } from '../../atoms/dialog'
import Typography from '../../atoms/Typography'
import { ExtBadge } from '../../atoms/ExtBadge'
import { GradientBorderButton } from '../../atoms/buttons/GradientBorderButton'
import { cn } from '../../../enviroment/lib/utils'
import { splitFileName } from '../../../enviroment/lib/fileUtils'

interface FullscreenPreviewDialogProps {
  isOpen: boolean
  onClose: () => void
  fileName: string
  details: string[]
  onDownload: () => void
  children: React.ReactNode
}

const FullscreenPreviewDialog: React.FC<FullscreenPreviewDialogProps> = ({
  isOpen,
  onClose,
  fileName,
  details,
  onDownload,
  children,
}) => {
  const { base, ext } = splitFileName(fileName)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        {/* nested above the data room dialog and the chat at z-50; radix routes esc to this layer only */}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[60] flex flex-col bg-gray-900 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <DialogPrimitive.Title className="sr-only">{fileName}</DialogPrimitive.Title>

          <div className="flex items-center justify-between border-b border-gray-500 px-6 py-4">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center">
                <Typography variant="base-bold" className="truncate text-gray-50">
                  {base}
                </Typography>
                {ext && <ExtBadge ext={ext} className="ml-2" />}
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                {details.map((detail, index) => (
                  <React.Fragment key={detail}>
                    {index > 0 && (
                      <Typography variant="sm" className="text-gray-200">
                        &#8226;
                      </Typography>
                    )}
                    <Typography variant="sm" className={cn('text-gray-200', index === 0 ? 'truncate' : 'shrink-0')}>
                      {detail}
                    </Typography>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <GradientBorderButton gradientSize="sm" onClick={onDownload}>
                <IconDownload className="h-4 w-4" />
                Download
              </GradientBorderButton>
              <button
                onClick={onClose}
                className="flex items-center justify-center gap-2 rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800"
                title="Exit Full Screen"
              >
                <IconMinimize className="h-5 w-5" color="#F5F6F6" stroke={2} />
                <Typography variant="sm" className="text-white">
                  Exit Full Screen
                </Typography>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1">{children}</div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export default FullscreenPreviewDialog
